/**
 * kBlobBytes - BROWSER-ONLY. A `Blob` / `File` as its bytes (`Uint8Array`), as TEXT, or as a `data:` URI, through ONE `FileReader`, the answer as two callbacks.
 *
 * WHY (SharePoint reuse programme WP-S2, 2026-10-05). The shared tree held five hand-rolled `FileReader.readAsArrayBuffer`
 * wrappers (`FileUploadControl`, `kDocPdf`, `kSPRestFiles` x2, `kSPUserPhoto`, `AIChats\ui\FileAttachButton`) and DeckBuilder six more,
 * each with its own idea of a failed read. This is the one lifted copy (from DeckBuilder's `DECKBSPAssetRead`, the one that refuses a
 * result that is not an ArrayBuffer). The old copies are not changed here; they adopt it in a later wave.
 *
 * USE: `kBlobBytes.Read(Blob, OnBytes, OnFailed)`; exactly one callback is called, once. Or `new kBlobBytes(Blob, OnBytes, OnFailed).Start()`
 * (the same thing, for a caller that keeps the instance). One instance reads ONE blob.
 * TEXT / DATA URI (2026-10-06, a DEFAULTED mode: `Read` and the constructor are unchanged and still mean bytes):
 *   `kBlobBytes.ReadText(Blob, OnText, OnFailed)`     the file as a string (`FileReader.readAsText`, UTF-8)
 *   `kBlobBytes.ReadDataUrl(Blob, OnUrl, OnFailed)`   the file as a `data:<type>;base64,...` string (`FileReader.readAsDataURL`)
 * Same rules for all three: exactly one callback, once; a result of the wrong kind is a failure sentence, never an empty answer.
 * A host without a `FileReader` (Node) gets `OnFailed`, never a throw. `ReaderFactory` is the test seam (a unit test supplies a stand-in).
 * Client decision 2026-10-05: this design replaces an earlier same-named file by another session; the `Read` and constructor calls are unchanged.
 *
 * What a second consumer would change: nothing.
 */

import { getCoreUtils } from "../kReusableCoreTS/kCoreErrorRegistry";

/** The part of `FileReader` this class uses (a test double needs only the members of the modes it exercises). */
export interface IBlobReader {
    result: unknown;
    addEventListener(Type: string, Handler: () => void): void;
    readAsArrayBuffer(Source: Blob): void;
    readAsText?(Source: Blob): void;
    readAsDataURL?(Source: Blob): void;
}

/** What the reader is asked for. The default, "bytes", is what the class always did. */
export type kBlobReadMode = "bytes" | "text" | "dataUrl";

export class kBlobBytes {
    /** Builds the reader for one read. Default: the browser's `FileReader`, or null where the host has none. */
    public static ReaderFactory: () => IBlobReader | null = (): IBlobReader | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            if (typeof FileReader === "undefined") { return null; }
            return new FileReader() as unknown as IBlobReader;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBlobBytes.ReaderFactory");
            return null;
        }
    };

    private _Source: Blob;
    private _OnBytes: (Bytes: Uint8Array) => void;
    private _OnText: ((Text: string) => void) | null = null;
    private _OnFailed: (Reason: string) => void;
    private _Mode: kBlobReadMode = "bytes";
    private _Reader: IBlobReader | null = null;
    private _Settled: boolean = false;

    public constructor(Source: Blob, OnBytes: (Bytes: Uint8Array) => void, OnFailed: (Reason: string) => void) {
        this._Source = Source;
        this._OnBytes = OnBytes;
        this._OnFailed = OnFailed;
    }

    /** Reads `Source` as bytes; exactly one of the two callbacks is called. */
    public static Read = (Source: Blob, OnBytes: (Bytes: Uint8Array) => void, OnFailed: (Reason: string) => void): void => {
        if (getCoreUtils().KErrorMode) { OnFailed("the product is stopped"); return; }
        try {
            new kBlobBytes(Source, OnBytes, OnFailed).Start();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBlobBytes.Read");
            OnFailed("the file could not be read");
        }
    };

    /** Reads `Source` as text (UTF-8); exactly one of the two callbacks is called. */
    public static ReadText = (Source: Blob, OnText: (Text: string) => void, OnFailed: (Reason: string) => void): void => {
        if (getCoreUtils().KErrorMode) { OnFailed("the product is stopped"); return; }
        try {
            kBlobBytes.ReadAs(Source, "text", OnText, OnFailed);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBlobBytes.ReadText");
            OnFailed("the file could not be read");
        }
    };

    /** Reads `Source` as a `data:` URI string; exactly one of the two callbacks is called. */
    public static ReadDataUrl = (Source: Blob, OnUrl: (DataUrl: string) => void, OnFailed: (Reason: string) => void): void => {
        if (getCoreUtils().KErrorMode) { OnFailed("the product is stopped"); return; }
        try {
            kBlobBytes.ReadAs(Source, "dataUrl", OnUrl, OnFailed);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBlobBytes.ReadDataUrl");
            OnFailed("the file could not be read");
        }
    };

    private static ReadAs = (Source: Blob, Mode: kBlobReadMode, OnText: (Text: string) => void, OnFailed: (Reason: string) => void): void => {
        if (getCoreUtils().KErrorMode) { OnFailed("the product is stopped"); return; }
        try {
            const One: kBlobBytes = new kBlobBytes(Source, kBlobBytes.NoBytes, OnFailed);
            One._Mode = Mode;
            One._OnText = OnText;
            One.Start();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBlobBytes.ReadAs");
            OnFailed("the file could not be read");
        }
    };

    /** DOCUMENTED EXCEPTION: a do-nothing placeholder for the unused bytes callback of the text modes. */
    private static NoBytes = (_Bytes: Uint8Array): void => { return; };

    public Start = (): void => {
        if (getCoreUtils().KErrorMode) { this.Fail("the product is stopped"); return; }
        try {
            if (this._Source === null || this._Source === undefined || typeof this._Source !== "object") { this.Fail("there is no file to read"); return; }
            this._Reader = kBlobBytes.ReaderFactory();
            if (this._Reader === null) { this.Fail("this browser cannot read files"); return; }
            this._Reader.addEventListener("loadend", this.OnLoadEnd);
            this.StartRead(this._Reader);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBlobBytes.Start");
            this.Fail("the file could not be read");
        }
    };

    /** The one `readAs...` call for this instance's mode; a reader that lacks it (a stand-in) is "cannot read files". */
    private StartRead = (Reader: IBlobReader): void => {
        if (getCoreUtils().KErrorMode) { this.Fail("the product is stopped"); return; }
        try {
            if (this._Mode === "text" && Reader.readAsText !== undefined) { Reader.readAsText(this._Source); return; }
            if (this._Mode === "dataUrl" && Reader.readAsDataURL !== undefined) { Reader.readAsDataURL(this._Source); return; }
            if (this._Mode === "bytes") { Reader.readAsArrayBuffer(this._Source); return; }
            this.Fail("this browser cannot read files");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBlobBytes.StartRead");
            this.Fail("the file could not be read");
        }
    };

    public OnLoadEnd = (): void => {
        if (getCoreUtils().KErrorMode) { this.Fail("the product is stopped"); return; }
        try {
            const Reader: IBlobReader | null = this._Reader;
            if (Reader === null) { this.Fail("the browser could not read the file"); return; }
            if (this._Mode !== "bytes") { this.SettleText(Reader.result); return; }
            if (Object.prototype.toString.call(Reader.result) !== "[object ArrayBuffer]") {
                this.Fail("the browser could not read the file");
                return;
            }
            this.Succeed(new Uint8Array(Reader.result as ArrayBuffer));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBlobBytes.OnLoadEnd");
            this.Fail("the file could not be read");
        }
    };

    /** A text or data-URI answer: only a string counts (a failed read leaves null). */
    private SettleText = (Result: unknown): void => {
        // DOCUMENTED EXCEPTION: a pure hand-off of a value checked here; called only from the guarded, wrapped OnLoadEnd.
        if (typeof Result !== "string" || this._OnText === null) { this.Fail("the browser could not read the file"); return; }
        if (this._Settled === true) { return; }
        this._Settled = true;
        this._OnText(Result);
    };

    private Succeed = (Bytes: Uint8Array): void => {
        // DOCUMENTED EXCEPTION: the settle hand-off, no guard (it releases the caller, also under the error gate).
        if (this._Settled === true) { return; }
        this._Settled = true;
        this._OnBytes(Bytes);
    };

    private Fail = (Reason: string): void => {
        // DOCUMENTED EXCEPTION: see Succeed. ERROR-SUPPRESSED-JUSTIFIED: an unreadable file is a defined outcome the caller words for the person.
        if (this._Settled === true) { return; }
        this._Settled = true;
        this._OnFailed(Reason);
    };
}
